// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { ProductListProductInformationStyles } from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPreviousPurchase from "@stanion/App/Components/Product/ProductPreviousPurchase";
import ProductListActions from "@stanion/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductInformation from "@stanion/Overrides/Widgets/ProductList/ProductListProductInformation";
import RecentPurchases from "@stanion/XDL/Icons/RecentPurchases";
import { color } from "@stanion/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    container?: InjectableCss;
    imageSection?: InjectableCss;
    infoSection?: InjectableCss;
    actionsSection?: InjectableCss;
    productListProductImageStyles?: ProductListProductImageStyles;
    productListProductInformation?: ProductListProductInformationStyles;
    productPreviousPurchase?: TypographyPresentationProps;
}

export const productCardStyles: ProductListProductCardStyles = {
    container: {
        css: css`
            display: flex;
            padding: 20px 0;
            margin-bottom: 20px;
            width: 100%;
            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    imageSection: {
        css: css`
            margin-right: 20px;
            width: 119px;
        `,
    },
    infoSection: {
        css: css`
            max-width: 556px;
            flex: 1 1 auto;
            margin-right: 20px;
        `,
    },
    actionsSection: {
        css: css`
            margin-top: 16px;
            max-width: 280px;
            min-width: 280px;
            flex: 1 1 auto;
            margin-left: auto;
        `,
    },
    productListProductImageStyles: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                max-width: 119px;
                align-items: center;
            `,
        },
        productImage: {
            linkWrappingImage: {
                css: css`
                    width: 119px;
                    height: 119px;
                    background: ${color("common.white")};
                    justify-content: center;
                    img {
                        width: 119px;
                        height: 119px;
                    }
                `,
            },
            img: {
                width: 119,
                height: 119,
            },
        },
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    // XNG-Changes
    return (
        <StyledWrapper {...styles.container} data-test-selector={`productListProductCard${props.product.id}`}>
            <StyledWrapper {...styles.imageSection}>
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={false}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                    extendedStyles={styles.productListProductImageStyles}
                />
                <ProductPreviousPurchase
                    product={props.product}
                    extendedStyles={styles.productPreviousPurchase}
                    customWidth="119px"
                />
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.infoSection}>
                <ProductListProductInformation
                    showBrand={props.showBrand}
                    showTitle={props.showTitle}
                    showPartNumbers={props.showPartNumbers}
                    showAvailability={props.showAvailability}
                    showAttributes={props.showAttributes}
                    extendedStyles={styles.productListProductInformation}
                    showImageBadges={props.showImageBadges}
                    maxImageBadges={props.maximumNumberOfImageBadges}
                    showTextBadges={props.showTextBadges}
                    maxTextBadges={props.maximumNumberOfTextBadges}
                />
                <ProductSearchScoreData />
            </StyledWrapper>
            <StyledWrapper {...styles.actionsSection}>
                <ProductListActions
                    showPrice={props.showPrice}
                    showAvailability={props.showAvailability}
                    showAddToList={props.showAddToList}
                    showCompare={props.showCompare}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductCard);
